import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FaSitemap, FaSearch, FaPaperPlane, FaVial, FaPen, FaChevronRight } from 'react-icons/fa';
import api from '../../services/api';
import { FinCheck } from '../../components/FinField';
import AlertaSedes, { RedesRelacionadas } from '../../components/AlertaSedes';
import '../../css/financeiro-parcela-pagar.css';

const TAMANHOS_PAGINA = [20, 50, 100];

// `sede` vem do banco como 1/0
const ehSede = (fornecedor) => fornecedor.sede === true || Number(fornecedor.sede) === 1;

const nomeDe = (fornecedor) => fornecedor.nomefantasia || fornecedor.nome || '';

// Busca sem acento e sem caixa: "São Paulo" tem de achar "sao paulo".
const normalizar = (valor) => String(valor || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const soDigitos = (valor) => String(valor || '').replace(/\D/g, '');

const dataBr = (valor) => {
    if (!valor) return '';
    const [data] = String(valor).replace('T', ' ').split(' ');
    const [ano, mes, dia] = String(data).split('-');
    if (!ano || !mes || !dia) return String(valor);
    return `${dia}/${mes}/${ano}`;
};

/**
 * Situação do formulário da filial, como a listagem devolve. A filial preenchida pelo link da
 * sede já chega como `em_revisao` ou `aprovado`: a listagem conta o envio daquele link para ela.
 */
const situacaoFormulario = (fornecedor) => fornecedor.status_disparo || 'nao_enviado';

const PREENCHIDAS = ['em_revisao', 'aprovado'];

// Badge ao lado da situação, que não muda: a resposta veio pelo link de outra filial da rede.
const badgePelaRede = (fornecedor) => (fornecedor.disparo_pela_rede
    ? <span className="fin-pp__minitag is-info" style={{ marginLeft: 6 }} title="Preenchido pelo link de outra filial da rede">pela rede</span>
    : null);

const pillFormulario = (fornecedor) => {
    switch (situacaoFormulario(fornecedor)) {
        case 'aprovado':
            return <><span className="fin-pp__pill is-ok">Aprovado {dataBr(fornecedor.disparo_aprovado_em)}</span>{badgePelaRede(fornecedor)}</>;
        case 'em_revisao':
            return <><span className="fin-pp__pill is-info">Em revisão {dataBr(fornecedor.disparo_preenchido_em)}</span>{badgePelaRede(fornecedor)}</>;
        case 'gerado':
            return <span className="fin-pp__pill is-ok">Link gerado {dataBr(fornecedor.disparo_link_gerado_em)}</span>;
        case 'enviado':
            return <span className="fin-pp__pill is-ok">Enviado {dataBr(fornecedor.disparo_enviado_em)}</span>;
        default:
            return <span className="fin-pp__pill is-muted">Não enviado</span>;
    }
};

/**
 * Aba "Redes" do Disparo de Formulário: os fornecedores ativos agrupados pela rede
 * (grupo de fornecedor), a sede primeiro, com a situação do formulário de cada filial.
 *
 * O agrupamento é feito aqui, sobre a mesma listagem do Painel de Fornecedores: cada
 * fornecedor traz os seus grupos, e quem está em mais de uma rede aparece em cada uma.
 * O envio continua sendo por ids — o que sai daqui são as sedes das redes marcadas,
 * e a confirmação, a prévia e a trava de quem já recebeu são as do disparo de sempre.
 */
export default function DisparoRedes({ token, bloqueado, recarga, onEnviar }) {
    const [fornecedores, setFornecedores] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(false);

    const [busca, setBusca] = useState('');
    // A maioria dos grupos tem um membro só: a própria sede. Escolher rede e filial só
    // muda alguma coisa onde há mais de uma, então a tela abre nelas.
    const [soComFiliais, setSoComFiliais] = useState(true);

    const [abertas, setAbertas] = useState(new Set());
    const [selecionadas, setSelecionadas] = useState(new Set());
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(TAMANHOS_PAGINA[0]);

    useEffect(() => {
        const controller = new AbortController();
        setCarregando(true);
        setErro(false);

        api({
            method: 'get',
            action: '/participante',
            token,
            params: {
                tipoCadastro: ['orcamento'],
                situacaoRegistro: 'ativo',
                include: 'fornecedorgrupo,fornecedorgrupos',
                incluir_status_disparo: true,
                // Sem `pageSize` o endpoint fica no modo de `limit`, e o agrupamento precisa
                // da base inteira numa chamada: uma rede pode ter filiais em qualquer página.
                limit: 10000,
            },
            signal: controller.signal,
        })
            .then(response => {
                setFornecedores(response.data?.data ?? []);
                setCarregando(false);
            })
            .catch(err => {
                if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
                setErro(true);
                setCarregando(false);
            });

        return () => controller.abort();
        // eslint-disable-next-line
    }, [recarga]);

    const redes = useMemo(() => {
        const mapa = new Map();

        fornecedores.forEach(fornecedor => {
            const grupos = (fornecedor.fornecedorgrupos && fornecedor.fornecedorgrupos.length > 0)
                ? fornecedor.fornecedorgrupos
                : (fornecedor.fornecedorgrupo ? [fornecedor.fornecedorgrupo] : []);

            grupos.forEach(grupo => {
                if (!grupo || grupo.id === undefined || grupo.id === null) return;

                const chave = String(grupo.id);
                if (!mapa.has(chave)) {
                    mapa.set(chave, { id: chave, nome: grupo.nome || 'Rede sem nome', filiais: [] });
                }
                mapa.get(chave).filiais.push(fornecedor);
            });
        });

        return [...mapa.values()]
            .map(rede => {
                const filiais = [...rede.filiais]
                    .sort((a, b) => (ehSede(b) - ehSede(a)) || nomeDe(a).localeCompare(nomeDe(b)));

                return {
                    ...rede,
                    filiais,
                    sedes: filiais.filter(ehSede),
                    preenchidas: filiais.filter(f => PREENCHIDAS.includes(situacaoFormulario(f))).length,
                };
            })
            .sort((a, b) => (b.filiais.length - a.filiais.length) || a.nome.localeCompare(b.nome));
    }, [fornecedores]);

    const visiveis = useMemo(() => {
        const termo = normalizar(busca.trim());
        const digitos = soDigitos(busca);

        return redes.filter(rede => {
            if (soComFiliais && rede.filiais.length < 2) return false;
            if (!termo) return true;
            if (normalizar(rede.nome).includes(termo)) return true;

            return rede.filiais.some(f => normalizar(f.nome).includes(termo)
                || normalizar(f.nomefantasia).includes(termo)
                || (digitos.length >= 3 && soDigitos(f.cnpj).includes(digitos)));
        });
    }, [redes, busca, soComFiliais]);

    // Trocar o recorte descarta a seleção: uma rede marcada que saiu da lista
    // continuaria recebendo o link sem aparecer na tela.
    useEffect(() => {
        setSelecionadas(new Set());
        setPagina(1);
    }, [busca, soComFiliais]);

    const totalPaginas = Math.max(1, Math.ceil(visiveis.length / porPagina));
    const paginadas = visiveis.slice((pagina - 1) * porPagina, pagina * porPagina);

    useEffect(() => {
        if (pagina > totalPaginas) setPagina(totalPaginas);
    }, [pagina, totalPaginas]);

    const alternar = (conjunto, setConjunto, id) => setConjunto(prev => {
        const novo = new Set(prev);
        novo.has(id) ? novo.delete(id) : novo.add(id);
        return novo;
    });

    // "Todas" é o recorte inteiro, não a página: quem filtra e marca tudo quer mandar para
    // o que o filtro trouxe. Rede sem sede fica fora — não há para quem mandar.
    const selecionaveis = visiveis.filter(rede => rede.sedes.length > 0);
    const todasMarcadas = selecionaveis.length > 0 && selecionaveis.every(rede => selecionadas.has(rede.id));
    const marcarTodas = (marcar) => setSelecionadas(marcar ? new Set(selecionaveis.map(rede => rede.id)) : new Set());

    // Redes em que cada fornecedor está: a mesma sede em mais de uma rede precisa aparecer como
    // tal, senão "3 redes · 2 sedes" na barra parece erro de conta.
    const redesPorFornecedor = useMemo(() => {
        const mapa = new Map();
        redes.forEach(rede => rede.filiais.forEach(filial => {
            const chave = String(filial.id);
            if (!mapa.has(chave)) mapa.set(chave, []);
            mapa.get(chave).push({ id: rede.id, nome: rede.nome });
        }));
        return mapa;
    }, [redes]);

    const outrasRedes = (fornecedor, rede) => (redesPorFornecedor.get(String(fornecedor.id)) || [])
        .filter(outra => outra.id !== rede.id);

    // Uma sede em duas redes marcadas recebe um link só. `repetidas` conta essas sedes, para a
    // barra dizer por que há menos sedes que redes.
    const { sedeIds, sedesRepetidas } = useMemo(() => {
        const vezes = new Map();
        redes.forEach(rede => {
            if (selecionadas.has(rede.id)) rede.sedes.forEach(sede => vezes.set(sede.id, (vezes.get(sede.id) || 0) + 1));
        });
        return {
            sedeIds: [...vezes.keys()],
            sedesRepetidas: [...vezes.values()].filter(n => n > 1).length,
        };
    }, [redes, selecionadas]);

    const totalFiliais = visiveis.reduce((soma, rede) => soma + rede.filiais.length, 0);

    return (
        <div className="fin-pp__region">
            <div className="fin-pp__cphead">
                <span className="fin-pp__cphead-ic"><FaSitemap size={18} /></span>
                <div style={{ minWidth: 0 }}>
                    <h2 className="fin-pp__cphead-title">Redes</h2>
                    <p className="fin-pp__cphead-sub">
                        {carregando || erro
                            ? 'Fornecedores ativos agrupados pela rede, a sede primeiro.'
                            : `${visiveis.length} rede${visiveis.length !== 1 ? 's' : ''} · ${totalFiliais} filia${totalFiliais !== 1 ? 'is' : 'l'}`}
                    </p>
                </div>
            </div>

            <div className="fin-pp__region-body">
                <div style={{ padding: '14px 14px 0', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div className="fin-pp__search-field" style={{ flex: '1 1 320px' }}>
                        <FaSearch className="fin-pp__search-ic" size={16} />
                        <input
                            className="fin-pp__input"
                            type="text"
                            placeholder="Buscar rede, filial ou CNPJ"
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                        />
                    </div>
                    <FinCheck
                        id="redesSoComFiliais"
                        label="Só redes com mais de uma filial"
                        checked={soComFiliais}
                        onChange={e => setSoComFiliais(e.target.checked)}
                    />
                    <FinCheck
                        id="redesTodas"
                        label={`Selecionar todas (${selecionaveis.length})`}
                        checked={todasMarcadas}
                        disabled={selecionaveis.length === 0}
                        onChange={e => marcarTodas(e.target.checked)}
                    />
                </div>

                {selecionadas.size > 0 && (
                    <div className="fin-pp__bulkbar" style={{ margin: '14px 14px 0' }}>
                        <span className="fin-pp__bulkbar-count">
                            {selecionadas.size} rede{selecionadas.size !== 1 ? 's' : ''} · {sedeIds.length} sede{sedeIds.length !== 1 ? 's' : ''}
                        </span>
                        {sedesRepetidas > 0 && (
                            <span style={{ fontSize: 12.5, color: 'var(--fin-mt)' }}>
                                {sedesRepetidas === 1
                                    ? '1 sede está em mais de uma rede marcada e recebe um link só'
                                    : `${sedesRepetidas} sedes estão em mais de uma rede marcada e recebem um link só`}
                            </span>
                        )}
                        <div className="fin-pp__bulkbar-actions">
                            <button
                                type="button"
                                className="fin-pp__btn fin-pp__btn--sm"
                                onClick={() => setSelecionadas(new Set())}
                            >
                                Limpar seleção
                            </button>
                            <button
                                type="button"
                                className="fin-pp__btn fin-pp__btn--sm"
                                disabled={bloqueado || sedeIds.length === 0}
                                onClick={() => onEnviar(sedeIds, true)}
                            >
                                <FaVial size={12} /> Simular
                            </button>
                            <button
                                type="button"
                                className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm"
                                disabled={bloqueado || sedeIds.length === 0}
                                onClick={() => onEnviar(sedeIds, false)}
                            >
                                <FaPaperPlane size={12} /> Enviar link às sedes
                            </button>
                        </div>
                    </div>
                )}

                {carregando ? (
                    <div className="fin-pp__empty">
                        <span className="fin-pp__loading"><span className="fin-pp__spin"></span>Carregando redes...</span>
                    </div>
                ) : erro ? (
                    <div className="fin-pp__empty">Não foi possível carregar as redes. Tente novamente.</div>
                ) : paginadas.length === 0 ? (
                    <div className="fin-pp__empty">Nenhuma rede encontrada</div>
                ) : (
                    <div className="fin-pp__groups" style={{ padding: 14 }}>
                        {paginadas.map(rede => {
                            const aberta = abertas.has(rede.id);
                            const semSede = rede.sedes.length === 0;
                            const n = rede.filiais.length;
                            // Redes relacionadas: as outras redes em que alguma sede desta também está.
                            const relacionadas = [];
                            rede.sedes.forEach(sede => outrasRedes(sede, rede).forEach(outra => {
                                if (!relacionadas.some(r => r.id === outra.id)) relacionadas.push({ ...outra, sede: nomeDe(sede) });
                            }));

                            return (
                                <div key={rede.id} className="fin-pp__grp">
                                    <div className="fin-pp__grp-head" onClick={() => alternar(abertas, setAbertas, rede.id)}>
                                        <input
                                            type="checkbox"
                                            checked={selecionadas.has(rede.id)}
                                            disabled={semSede}
                                            title={semSede
                                                ? 'Rede sem sede marcada: não há para quem mandar o link'
                                                : 'Marcar para enviar o link à sede'}
                                            onClick={e => e.stopPropagation()}
                                            onChange={() => alternar(selecionadas, setSelecionadas, rede.id)}
                                        />
                                        <Link
                                            to={'/cadastroFornecedorGrupo/' + rede.id}
                                            className="fin-pp__rowbtn"
                                            title="Abrir o cadastro da rede"
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <FaPen size={13} />
                                        </Link>
                                        <span className="fin-pp__grp-ic"><FaSitemap size={15} /></span>
                                        <span className="fin-pp__grp-name">{rede.nome}</span>
                                        <span className="fin-pp__grp-badge">{n} filia{n !== 1 ? 'is' : 'l'}</span>
                                        {semSede && <span className="fin-pp__pill is-warn">Sem sede</span>}
                                        {rede.sedes.length === 1 && (
                                            <span className="fin-pp__pill is-info">Sede: {nomeDe(rede.sedes[0])}</span>
                                        )}
                                        {rede.sedes.length > 1 && (
                                            <span className="fin-pp__pill is-info">{rede.sedes.length} sedes</span>
                                        )}
                                        <RedesRelacionadas relacionadas={relacionadas} />
                                        <AlertaSedes sedes={rede.sedes} grupoId={rede.id} />
                                        <span className="fin-pp__grp-total">
                                            Preenchidas: <strong>{rede.preenchidas}/{n}</strong>
                                        </span>
                                        {/* Ícone, não o caractere "▶": o `is-open` do CSS gira os 90°. */}
                                        <span className={'fin-pp__grp-caret' + (aberta ? ' is-open' : '')}>
                                            <FaChevronRight size={12} />
                                        </span>
                                    </div>

                                    {aberta && (
                                        <div className="fin-pp__tblwrap">
                                            <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 60 }}></th>
                                                        <th>Código</th>
                                                        <th>Filial</th>
                                                        <th>CNPJ</th>
                                                        <th>Formulário</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {rede.filiais.map(filial => (
                                                        <tr key={filial.id}>
                                                            <td>
                                                                <Link
                                                                    to={'/cadastroFornecedor/' + filial.id}
                                                                    className="fin-pp__rowbtn"
                                                                    title="Abrir o cadastro do fornecedor"
                                                                >
                                                                    <FaPen size={14} />
                                                                </Link>
                                                            </td>
                                                            <td className="fin-pp__cell-muted">{filial.codigo ?? ''}</td>
                                                            <td>
                                                                <span className="fin-pp__cell-strong">{nomeDe(filial)}</span>
                                                                {ehSede(filial) && (
                                                                    <span className="fin-pp__pill is-info" style={{ marginLeft: 8 }}>Sede</span>
                                                                )}
                                                                {ehSede(filial) && outrasRedes(filial, rede).length > 0 && (
                                                                    <span
                                                                        className="fin-pp__minitag is-info"
                                                                        style={{ marginLeft: 6 }}
                                                                        title={'Também é sede de: ' + outrasRedes(filial, rede).map(outra => outra.nome).join(', ')}
                                                                    >
                                                                        em {1 + outrasRedes(filial, rede).length} redes
                                                                    </span>
                                                                )}
                                                                {filial.nomefantasia && filial.nome && filial.nomefantasia !== filial.nome && (
                                                                    <div className="fin-pp__cell-muted" style={{ fontSize: 12 }}>{filial.nome}</div>
                                                                )}
                                                            </td>
                                                            <td className="fin-pp__cell-muted">{filial.cnpj || filial.cpf || ''}</td>
                                                            <td>{pillFormulario(filial)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="fin-pp__pagination">
                    <div className="fin-pp__pagination-info">{visiveis.length} redes</div>
                    <div className="fin-pp__pagination-ctrls">
                        <select
                            className="fin-pp__select"
                            style={{ height: 34 }}
                            value={porPagina}
                            onChange={e => { setPorPagina(Number(e.target.value)); setPagina(1); }}
                        >
                            {TAMANHOS_PAGINA.map(tamanho => (
                                <option key={tamanho} value={tamanho}>{tamanho} / pág</option>
                            ))}
                        </select>
                        <button className="fin-pp__btn fin-pp__btn--sm" disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>Anterior</button>
                        <span className="fin-pp__pagination-info">{pagina} / {totalPaginas}</span>
                        <button className="fin-pp__btn fin-pp__btn--sm" disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>Próxima</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
